import { useRef } from 'react';
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import { axiosInstance } from 'service';
import { LOG_ACTIONS } from '../components/AuditLogs/LogActions';

const STORAGE_KEY = 'logQueue';

const usePostLog = () => {
  const logs = localStorage.getItem(STORAGE_KEY);
  const parsedJSON = logs ? JSON.parse(logs) : [];
  const logQueue = useRef(parsedJSON);
  const isSending = useRef(false);

  const saveToLocalStorage = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(logQueue.current));
  };

  const logEvent = (event) => {
    console.log('event', event);
    let modifiedPayload = event.payload;
    if (event.action === LOG_ACTIONS.FILTER) {
      modifiedPayload = {
        ...event.payload,
        channel: event?.payload?.category || [],
      };
      delete modifiedPayload.category;
    } else if (event.action === LOG_ACTIONS.DRAWER) {
      console.log('mode', modifiedPayload);
    }
    logQueue.current.push({
      ...event,
      payload: modifiedPayload,
      timestamp: new Date().toISOString(),
    });
    saveToLocalStorage();
  };

  const postLog = async (logData) => {
    console.log('🚀 ~ postLog ~ logData:', logData);
    const cookies = document.cookie ? document.cookie.split(';') : null;
    let idToken = '';
    if (cookies) {
      const idCookie = cookies.find((cookie) => cookie.includes('ent-abs-itkn'));
      const idField = idCookie ? idCookie.split('=') : null;
      idToken = idField ? idField[1] : '';
    }
    const response = await axiosInstance.post('/audit/log-user-action', logData, {
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
    return response.data;
  };

  const mutation: UseMutationResult<void, Error, any[]> = useMutation({
    mutationFn: postLog,
    onSuccess: () => {
      localStorage.removeItem(STORAGE_KEY);
    },
    onError: () => {
      saveToLocalStorage();
    },
  });

  const sendLogs = () => {
    const logsInLocal = localStorage.getItem(STORAGE_KEY);
    const parsedLogs = logsInLocal ? JSON.parse(logsInLocal) : [];
    if (parsedLogs.length === 0 || isSending.current) return;
    isSending.current = true;
    const logsToSend = [...parsedLogs];
    logQueue.current = [];
    saveToLocalStorage();
    mutation.mutate(logsToSend, {
      onSettled: () => {
        isSending.current = false;
      },
    });
  };

  return {
    sendLog: logEvent,
    logsApi: sendLogs,
    LOG_ACTIONS,
  };
};

export const useGetAuditData = (dates, userNames, actions, page, pageSize) => {
  const queryParams = { dates, userNames, actions, page, pageSize };
  const tableData = useQuery({
    queryKey: ['get-audit-data', queryParams],
    queryFn: async () => {
      console.log('dates', dates);
      const params = {
        start: dates[0]?.format('YYYY-MM-DD'),
        end: dates[1]?.format('YYYY-MM-DD'),
        userName: userNames,
        action: actions,
        page,
        pageSize,
      };
      const { data } = await axiosInstance.get('/audit/get-audit-data', { params });
      return data;
    },
    enabled: dates[0] != null && dates[1] != null,
  });
  return {
    tableData,
  };
};

export const useGetAuditChartData = (dates, userNames, actions) => {
  const queryParams = { dates, userNames, actions };
  const top10Users = useQuery({
    queryKey: ['getTopAuditUsers', queryParams],
    queryFn: async () => {
      const params = {
        start: dates[0]?.format('YYYY-MM-DD'),
        end: dates[1]?.format('YYYY-MM-DD'),
        userName: userNames,
        action: actions,
      };
      const { data } = await axiosInstance.get('/audit/getTopAuditUsers', { params });
      return data;
    },
    enabled: dates[0] != null && dates[1] != null,
  });

  const actionDistribution = useQuery({
    queryKey: ['getAuditActionDistribution', queryParams],
    queryFn: async () => {
      const params = {
        start: dates[0]?.format('YYYY-MM-DD'),
        end: dates[1]?.format('YYYY-MM-DD'),
        userName: userNames,
        action: actions,
      };
      const { data } = await axiosInstance.get('/audit/getAuditActionDistribution', { params });
      return data;
    },
    enabled: dates[0] != null && dates[1] != null,
  });

  return {
    top10Users,
    actionDistribution,
  };
};

export const fetchDefaultDates = async () => {
  const response = await axiosInstance.get('/audit/get-initial-dates');
  return response.data;
};

const fetchFilters = async ({ queryKey }) => {
  const [, params] = queryKey;
  const { data } = await axiosInstance.get('/audit/filters', { params });
  return data;
};

export const useFilter = (dates, action: string[] = [], user: string[] = []) => {
  const params = {
    startDate: dates[0]?.format('YYYY-MM-DD'),
    endDate: dates[1]?.format('YYYY-MM-DD'),
    action,
    user,
  };
  const filters = useQuery({ queryKey: ['filters', params], queryFn: fetchFilters });
  return { filters };
};

const fetchUserActivityData = async ({ queryKey }) => {
  const [, params] = queryKey;
  const response = await axiosInstance.get('/audit/getUserActivityChart', { params });
  return response.data;
};

export const useActvityFetch = (dateType, type, userNames, actions) => {
  const data = useQuery({ queryKey: ['params', { dateType, type, userNames, actions }], queryFn: fetchUserActivityData, enabled: true });
  return { data };
};

const fetchActivityMetricsData = async ({ queryKey }) => {
  const [, params] = queryKey;
  const response = await axiosInstance.get('/audit/getAuditMetrics', { params });
  return response.data;
};

export const useActvityMetrics = (dates) => {
  const params = {
    startDate: dates[0]?.format('YYYY-MM-DD'),
    endDate: dates[1]?.format('YYYY-MM-DD'),
  };
  const data = useQuery({ queryKey: ['params', { ...params }], queryFn: fetchActivityMetricsData, enabled: dates[0] != null && dates[1] != null });
  return { data };
};

export default usePostLog;
